(() => {
  const bannerSelector = '#shopify-pc__banner';
  const preferencesSelector = '#shopify-pc__prefs';
  const manageButtonSelector = '#shopify-pc__banner__btn-manage-prefs';
  const footerTriggerSelector = '[data-cookie-consent-open]';
  const bannerTemplateSelector = '[data-cookie-consent-template]';
  const dismissedClass = 'cookie-consent--dismissed';
  const preferencesOpenClass = 'cookie-prefs--open';
  const preferencesClosedClass = 'cookie-prefs--closed';
  const bannerPreferencesClass = 'cookie-consent--preferences-open';
  const sessionKey = 'alma-cookie-banner-dismissed';
  let restoreBannerAfterPreferences = false;

  const getSessionDismissed = () => {
    try {
      return window.sessionStorage.getItem(sessionKey) === 'true';
    } catch {
      return false;
    }
  };

  const setSessionDismissed = (dismissed) => {
    try {
      if (dismissed) {
        window.sessionStorage.setItem(sessionKey, 'true');
      } else {
        window.sessionStorage.removeItem(sessionKey);
      }
    } catch {
      // The banner remains functional when storage is unavailable.
    }
  };

  const movePreferencesAbovePageContent = (preferences) => {
    if (preferences.parentElement !== document.body || preferences !== document.body.lastElementChild) {
      document.body.append(preferences);
    }
  };

  const dismissBanner = (banner) => {
    banner.classList.add(dismissedClass);
    banner.setAttribute('aria-hidden', 'true');
    setSessionDismissed(true);
    document.querySelector(footerTriggerSelector)?.focus({ preventScroll: true });
  };

  const createCustomBanner = () => {
    const template = document.querySelector(bannerTemplateSelector);
    if (!(template instanceof HTMLTemplateElement)) return null;

    const banner = template.content.firstElementChild?.cloneNode(true);
    if (!(banner instanceof HTMLElement)) return null;

    document.body.append(banner);
    enhanceBanner(banner);
    return banner;
  };

  const setCustomConsent = (banner, accepted) => {
    const customerPrivacy = window.Shopify?.customerPrivacy;
    if (typeof customerPrivacy?.setTrackingConsent !== 'function') return;

    customerPrivacy.setTrackingConsent(
      {
        analytics: accepted,
        marketing: accepted,
        preferences: accepted,
      },
      () => dismissBanner(banner)
    );
  };

  const replaceHeadingElement = (banner) => {
    const heading = banner.querySelector('#shopify-pc__banner__body-title');
    if (!(heading instanceof HTMLHeadingElement)) return;

    const replacement = document.createElement('p');
    replacement.id = heading.id;
    replacement.className = heading.className;
    replacement.dataset.elementId = heading.dataset.elementId;
    replacement.innerHTML = heading.innerHTML;

    heading.replaceWith(replacement);
  };

  const enhanceBanner = (banner) => {
    if (!(banner instanceof HTMLElement) || banner.dataset.almaCookieReady === 'true') return;

    banner.dataset.almaCookieReady = 'true';
    
    replaceHeadingElement(banner);

    if (getSessionDismissed()) {
      banner.classList.add(dismissedClass);
      banner.setAttribute('aria-hidden', 'true');
    }

    const wrapper = banner.querySelector('.shopify-pc__banner__wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    const footerTrigger = document.querySelector(footerTriggerSelector);
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'cookie-consent__close';
    closeButton.setAttribute('aria-label', footerTrigger?.dataset.cookieCloseLabel || 'Close cookie banner');
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M3 9L9 3M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    closeButton.addEventListener('click', () => dismissBanner(banner));
    wrapper.append(closeButton);
  };

  const enhancePreferences = (preferences) => {
    if (!(preferences instanceof HTMLElement)) return;

    const closeButton = preferences.querySelector('#shopify-pc__prefs__header-close');
    if (closeButton instanceof HTMLButtonElement && closeButton.dataset.almaIconReady !== 'true') {
      closeButton.dataset.almaIconReady = 'true';
      closeButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M3 9L9 3M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    if (
      preferences.dataset.almaCookieReady === 'true' &&
      preferences.querySelector('.cookie-prefs__accordion-trigger')
    ) {
      return;
    }

    preferences.dataset.almaCookieReady = 'true';

    if (preferences.style.display && preferences.style.display !== 'none') {
      movePreferencesAbovePageContent(preferences);
      preferences.classList.remove(preferencesClosedClass);
      preferences.classList.add(preferencesOpenClass);
      preferences.removeAttribute('aria-hidden');
    }

    preferences.querySelectorAll('.shopify-pc__prefs__option').forEach((option) => {
      if (option.querySelector('.cookie-prefs__accordion-trigger')) return;

      const label = option.querySelector('label');
      const panel = option.querySelector(':scope > p');
      if (!(label instanceof HTMLLabelElement) || !(panel instanceof HTMLParagraphElement)) return;

      const labelText = Array.from(label.childNodes)
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent?.trim())
        .filter(Boolean)
        .join(' ');

      Array.from(label.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) node.remove();
      });

      const optionHeader = document.createElement('div');
      optionHeader.className = 'cookie-prefs__option-header';

      const accordionTrigger = document.createElement('button');
      accordionTrigger.type = 'button';
      accordionTrigger.className = 'cookie-prefs__accordion-trigger';
      accordionTrigger.setAttribute('aria-expanded', 'false');

      const icon = document.createElement('span');
      icon.className = 'cookie-prefs__accordion-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '+';

      const triggerLabel = document.createElement('span');
      triggerLabel.textContent = labelText;

      const panelId = `${option.id}-panel`;
      panel.id = panelId;
      panel.hidden = true;
      accordionTrigger.setAttribute('aria-controls', panelId);
      accordionTrigger.append(icon, triggerLabel);

      label.classList.add('cookie-prefs__switch');
      label.setAttribute('aria-label', labelText);
      optionHeader.append(accordionTrigger, label);
      option.prepend(optionHeader);

      accordionTrigger.addEventListener('click', () => {
        const expanded = accordionTrigger.getAttribute('aria-expanded') === 'true';
        accordionTrigger.setAttribute('aria-expanded', String(!expanded));
        icon.textContent = expanded ? '+' : '−';
        panel.hidden = expanded;
        option.classList.toggle('is-expanded', !expanded);
      });
    });
  };

  const setPreferencesElementState = (preferences, open) => {
    enhancePreferences(preferences);
    preferences.classList.toggle(preferencesOpenClass, open);
    preferences.classList.toggle(preferencesClosedClass, !open);

    if (open) {
      movePreferencesAbovePageContent(preferences);
      preferences.style.display = 'block';
      preferences.removeAttribute('aria-hidden');
    } else {
      preferences.style.display = 'none';
      preferences.setAttribute('aria-hidden', 'true');
    }
  };

  const setPreferencesOpen = (open, activePreferences) => {
    const preferencesElements = Array.from(document.querySelectorAll(preferencesSelector)).filter(
      (preferences) => preferences instanceof HTMLElement
    );

    if (!preferencesElements.length) return;

    if (!open) {
      preferencesElements.forEach((preferences) => setPreferencesElementState(preferences, false));
      return;
    }

    const preferences =
      activePreferences instanceof HTMLElement
        ? activePreferences
        : preferencesElements.findLast(
            (element) => element.style.display && element.style.display !== 'none'
          ) || preferencesElements.at(-1);

    preferencesElements.forEach((element) => {
      setPreferencesElementState(element, element === preferences);
    });
  };

  const openNativePreferences = () => {
    const preferencesElements = Array.from(document.querySelectorAll(preferencesSelector)).filter(
      (preferences) => preferences instanceof HTMLElement
    );
    const preferences =
      preferencesElements.findLast(
        (element) =>
          !element.classList.contains(preferencesClosedClass) &&
          element.style.display &&
          element.style.display !== 'none'
      ) || preferencesElements.at(-1);

    if (preferences) setPreferencesOpen(true, preferences);
  };

  const showStoredPreferences = () => {
    const privacyBanner = window.privacyBanner;
    if (typeof privacyBanner?.showPreferences !== 'function') return false;

    try {
      Promise.resolve(privacyBanner.showPreferences()).then(() => {
        window.requestAnimationFrame(openNativePreferences);
      });
      return true;
    } catch {
      return false;
    }
  };

  const hideBannerForPreferences = () => {
    const banner = document.querySelector(bannerSelector);
    if (!(banner instanceof HTMLElement)) return;

    restoreBannerAfterPreferences =
      window.getComputedStyle(banner).display !== 'none' && !banner.classList.contains(dismissedClass);

    banner.classList.add(bannerPreferencesClass);
    banner.setAttribute('aria-hidden', 'true');
  };

  const closePreferences = (restoreBanner) => {
    const banner = document.querySelector(bannerSelector);

    setPreferencesOpen(false);

    if (!(banner instanceof HTMLElement)) return;

    banner.classList.remove(bannerPreferencesClass);

    if (restoreBanner && restoreBannerAfterPreferences) {
      banner.style.display = 'block';
      banner.removeAttribute('aria-hidden');
      banner.querySelector('.cookie-consent__close')?.focus({ preventScroll: true });
    }

    restoreBannerAfterPreferences = false;
  };

  const showBannerOrPreferences = () => {
    const banner = document.querySelector(bannerSelector) || createCustomBanner();

    if (banner instanceof HTMLElement) {
      setSessionDismissed(false);
      banner.classList.remove(dismissedClass);
      banner.classList.remove(bannerPreferencesClass);
      banner.style.display = 'block';
      banner.removeAttribute('aria-hidden');
      banner.querySelector('.cookie-consent__close')?.focus({ preventScroll: true });
      return;
    }
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(footerTriggerSelector);
    if (!trigger) return;

    event.preventDefault();
    showBannerOrPreferences();
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const customBanner = event.target.closest('[data-alma-custom-banner]');

      if (customBanner instanceof HTMLElement && event.target.closest('#shopify-pc__banner__btn-accept')) {
        event.preventDefault();
        setCustomConsent(customBanner, true);
        return;
      }

      if (customBanner instanceof HTMLElement && event.target.closest('#shopify-pc__banner__btn-decline')) {
        event.preventDefault();
        setCustomConsent(customBanner, false);
        return;
      }

      if (event.target.closest(manageButtonSelector)) {
        hideBannerForPreferences();
        if (customBanner instanceof HTMLElement) {
          event.preventDefault();
          showStoredPreferences();
        } else {
          window.setTimeout(openNativePreferences, 0);
        }
        return;
      }

      if (event.target.closest('#shopify-pc__prefs__header-close')) {
        window.setTimeout(() => closePreferences(true), 0);
        return;
      }

      if (event.target.closest('#shopify-pc__prefs__header-save')) {
        window.setTimeout(() => closePreferences(false), 0);
        return;
      }

      if (event.target.closest('#shopify-pc__prefs__overlay')) {
        window.setTimeout(() => closePreferences(true), 0);
      }
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape') closePreferences(true);
    },
    true
  );

  const enhancePrivacyUi = () => {
    const banner = document.querySelector(bannerSelector);
    const preferencesElements = Array.from(document.querySelectorAll(preferencesSelector)).filter(
      (preferences) => preferences instanceof HTMLElement
    );

    if (banner) enhanceBanner(banner);
    preferencesElements.forEach(enhancePreferences);

    const openPreferences = preferencesElements.filter((preferences) =>
      preferences.classList.contains(preferencesOpenClass)
    );
    const activePreferences = openPreferences.at(-1);

    if (openPreferences.length > 1 && activePreferences) {
      setPreferencesOpen(true, activePreferences);
    }

    if (
      banner instanceof HTMLElement &&
      activePreferences instanceof HTMLElement
    ) {
      banner.classList.add(bannerPreferencesClass);
      banner.setAttribute('aria-hidden', 'true');
    }
  };

  enhancePrivacyUi();

  let enhancementFrame;
  const observer = new MutationObserver(() => {
    window.cancelAnimationFrame(enhancementFrame);
    enhancementFrame = window.requestAnimationFrame(enhancePrivacyUi);
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
